import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Icon } from '@cbrebuild/blocks';
import LabeledItem from '../../../nucleus/labeled-item/labeled-item';
import formatNumberWithCommas from '../../../utils/format-number-with-commas';
import formatAsCurrency from '../../../utils/format-as-currency';
import Percent from '../../../nucleus/formats/percent';
import { validateAddress } from '../voucher-validation';
import { useRequiredFieldsRegistry } from './RequiredFieldsContext';
import { isNumericOutOfRange, isDateOutOfRange, formatOutOfRangeMessage } from '../../../utils/broker-verification-utils';

const RequiredLabeledItem = ({
  format,
  item,
  isReview,
  notRequired,
  invalid,
  label,
  fieldId,
  section,
  subSection,
  editTarget,
  linkTo,
  controlType,
  options,
  bounds,
  rangeFormat,
  rangeUnit,
  requiredMessage,
  forceRegister,
  hideDisplay,
  missingMessage,
  documents,
}) => {
  // Treats an empty array (e.g. a list of selected attachment ids) as "missing", same as a
  // falsy scalar -- `![]` is `false` in JS, so this would otherwise never count as required.
  const isEmptyValue = val => (Array.isArray(val) ? val.length === 0 : !val && val !== 0);
  let showError = false;
  let updatedItem = item;
  if (invalid) {
    // Caller has a real (non-empty) value that fails its own validation (e.g. Pursuit ID's
    // format check) -- force the error state WITHOUT blanking `item`, so both the static
    // review value and the "Before you submit" panel's editable input keep showing the
    // actual value the user entered (matching the live voucher tab), instead of going blank.
    showError = true;
    if (isReview) {
      localStorage.setItem('voucher_valid', JSON.stringify(false));
    }
  } else if (format !== 'bool' && isEmptyValue(item) && !notRequired) {
    updatedItem = missingMessage || `${label} is required`;
    showError = true;
    if (isReview) {
      localStorage.setItem('voucher_valid', JSON.stringify(false));
    }
  }
  // A field only counts as "resolved" once it has a genuine value AND -- when bounds are
  // available from the broker-verification bounds API -- that value is within range.
  // Advisory only (BR-F2): never affects `voucher_valid`/showError above.
  const hasGenuineValue = format === 'bool' || !isEmptyValue(item);
  const isOutOfRange = hasGenuineValue && !!bounds && (isNumericOutOfRange(item, bounds) || isDateOutOfRange(item, bounds));
  const rangeMessage = isOutOfRange ? formatOutOfRangeMessage(bounds, rangeFormat, rangeUnit) : null;
  if (!isEmptyValue(item) || format === 'bool') {
    switch (format) {
      case 'attachmentList':
        if (documents && documents.length > 0) {
          updatedItem = documents.length === 1
            ? <span className="required-labeled-item__documents">{documents[0].name}</span>
            : (
              <ul className="required-labeled-item__documents required-labeled-item__documents--list">
                {documents.map(document => <li key={document.id}>{document.name}</li>)}
              </ul>
            );
        } else {
          updatedItem = `${item.length} document${item.length > 1 ? 's' : ''} selected`;
        }
        break;
      // Custom Lease Escalation schedule (`compData.rent_escalation_custom`) -- `item` is an
      // array of plain row objects, which LabeledItem can't render directly as a child
      // (React throws "Objects are not valid as a React child"), so this collapses it to a
      // summary count for the on-page display only. `seedValue` (captured above, before this
      // switch runs) still carries the raw array through to the "Before you submit" panel.
      case 'escalationSchedule':
        updatedItem = `${item.length} rent step${item.length === 1 ? '' : 's'} added`;
        break;
      case 'address':
        if (validateAddress(item)) {
          updatedItem = (
            <>
              {item.address} {item.address2 && <>{item.address2}</>} <br />
              {item.city}, {item.state_abbreviation || item.territory} {item.postal_code || item.postal}
            </>
          );
        } else {
          updatedItem = `${label} is required`;
          showError = true;
          if (isReview) {
            localStorage.setItem('voucher_valid', JSON.stringify(false));
          }
        }
        break;
      case 'bool':
        updatedItem = item ? 'Yes' : 'No';
        break;
      case 'currency':
        updatedItem = formatAsCurrency(item);
        break;
      case 'number':
        updatedItem = item && formatNumberWithCommas(Number(item).toFixed(0));
        break;
      case 'number2dp': {
        const n2 = parseFloat(item);
        if (!Number.isNaN(n2)) {
          const [int2, dec2] = n2.toFixed(2).split('.');
          updatedItem = `${formatNumberWithCommas(int2)}.${dec2}`;
        }
        break;
      }
      case 'number3dp': {
        const n3 = parseFloat(item);
        if (!Number.isNaN(n3)) {
          const trimmed3 = parseFloat(n3.toFixed(3)).toString();
          const [int3, dec3] = trimmed3.split('.');
          updatedItem = dec3 ? `${formatNumberWithCommas(int3)}.${dec3}` : formatNumberWithCommas(int3);
        }
        break;
      }
      case 'select': {
        // item holds the raw stored value (e.g. 'true'/'false'); show its option label instead.
        const matchedOption = options?.find(option => String(option.value) === String(item));
        updatedItem = matchedOption ? matchedOption.display_name : item;
        break;
      }
      case 'percent':
        updatedItem = <Percent decimalRounding={2} value={Number(item)} />;
        break;
      case 'percent3dp':
        updatedItem = <Percent decimalRounding={3} value={Number(item)} />;
        break;
      case 'shortDate':
        updatedItem = moment(item).format('M/D/YY');
        break;
      case 'longDate':
        updatedItem = moment(item).format('MMM D, YYYY');
        break;
      default:
    }
  }

  const registry = useRequiredFieldsRegistry();
  const registerRequiredField = registry?.registerRequiredField;
  const unregisterRequiredField = registry?.unregisterRequiredField;
  // Latches to `true` the first time this field is found missing/out-of-range (see the
  // registration effect below) and never resets -- gates whether this field EVER shows up
  // in the "Before you submit" panel at all.
  const everNeededAttentionRef = useRef(false);
  const editTargetKey = JSON.stringify(editTarget);
  const boundsKey = JSON.stringify(bounds);
  // Stringify `options` for the dependency array (like `editTargetKey` above) instead of
  // depending on the raw array reference: callers commonly pass an inline array literal
  // (e.g. `options={[...]}`), which is a new reference every render. Depending on that
  // reference directly caused this effect's cleanup (unregister) and body (re-register) to
  // run on every render forever, triggering "Maximum update depth exceeded".
  const optionsKey = JSON.stringify(options);
  // Raw current value to seed the "Before you submit" panel's control with, so an
  // already-filled-in field shows its real value there (not blank) on first render --
  // e.g. after a page refresh or switching tabs, before the user touches the panel itself.
  // `item` is already the raw underlying value for every controlType except 'boolean',
  // whose one call site (Account Client) pre-formats it to a 'Yes'/'No' display string --
  // convert that back to the 'true'/'false' strings the panel's RadioGroup expects.
  let seedValue = item;
  if (controlType === 'boolean') {
    if (item === true || item === 'Yes') seedValue = 'true';
    else if (item === false || item === 'No') seedValue = 'false';
    else seedValue = '';
  }
  useEffect(() => {
    if (!fieldId || !registerRequiredField) return;
    // Only show this field in the "Before you submit" panel if it has EVER needed
    // attention (genuinely missing, or out of its resolved bounds) since this component
    // mounted -- a field the user already filled in correctly earlier in the voucher flow
    // (and that's never gone out of range) never shows up here at all, per product
    // feedback (2026-07-31) that already-complete fields shouldn't clutter this panel just
    // because the user happened to revisit the Review screen. `everNeededAttentionRef`
    // latches to `true` the first time either condition is true (handling `bounds` arriving
    // asynchronously from the broker-verification bounds API after mount, not just this
    // component's very first render) and never resets -- once a field has been flagged, it
    // stays registered (with `complete` toggling as it's fixed/re-broken) so the user still
    // sees it flip to a green checkmark and can keep re-editing it here, instead of it
    // disappearing, per the 2026-07-31 "keep it visible" product decision.
    // Only latch (see `everNeededAttentionRef` above) once `isReview` is true -- i.e. once
    // the CALLER considers its own underlying data genuinely loaded, not just "whatever
    // `item` happens to be on this render". Several fields (e.g. CompanyContactReview.js's
    // company/contact info) fetch their own data via a separate request AFTER this
    // component has already mounted, so `item` is transiently null/undefined for the first
    // render or two purely because that fetch hasn't resolved yet -- NOT because the user
    // actually left the field blank in the voucher flow. Without this guard, that transient
    // "not loaded yet" blip was misread as "this field was missing when the user arrived at
    // Review", permanently (and incorrectly) latching already-filled-in fields into the
    // panel the moment their data loaded a render later (2026-07-31 bug: the whole Tenant
    // Information/Landlord Information sections showing up fully checked off).
    // `forceRegister` lets a caller flag this field as belonging to a still-incomplete
    // group (e.g. "Is there a commission agreement?" alongside its own required attachment
    // upload) even though ITS OWN value already resolves without error -- otherwise it
    // stays hidden here while its sibling still shows, orphaning that sibling's message.
    if (isReview && (showError || isOutOfRange || forceRegister)) everNeededAttentionRef.current = true;
    if (!everNeededAttentionRef.current) return;
    registerRequiredField(fieldId, {
      section,
      subSection,
      label,
      editTarget,
      linkTo,
      controlType,
      options,
      bounds,
      value: seedValue,
      complete: !showError && !isOutOfRange,
      isRangeWarning: isOutOfRange && !showError,
      message: isOutOfRange ? rangeMessage : undefined,
      // Lets a caller override the generic "<label> is required" panel message (e.g. a
      // format-validation error like Pursuit ID's) with the SAME wording shown inline on
      // the page, instead of the generic fallback -- only applies while `showError`, same
      // as that fallback (see `renderFieldMessage` in `RequiredFieldsSummary.js`).
      requiredMessage: showError ? requiredMessage : undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fieldId, showError, isOutOfRange, rangeMessage, section, subSection, label, editTargetKey, linkTo, controlType, optionsKey,
    boundsKey, registerRequiredField, unregisterRequiredField, seedValue, isReview, requiredMessage, forceRegister,
  ]);


  // Unregister only on true unmount (e.g. navigating away from the Review page), using a
  // ref so the cleanup always calls unregister with the latest fieldId/callback without
  // re-running (and thus re-unregistering) on every render.
  const unmountRef = useRef();
  unmountRef.current = () => {
    // Harmless no-op if this field was never registered above (never needed attention).
    if (fieldId && unregisterRequiredField) unregisterRequiredField(fieldId);
  };
  useEffect(() => () => unmountRef.current(), []);

  // Registration above (for the "Before you submit" panel) still runs -- this only
  // skips the on-page value, for fields whose info is already shown elsewhere (e.g.
  // "Lease Escalation Breakdown"'s row count duplicating the "Escalations Schedule" table).
  if (hideDisplay) return null;

  return (
    <div className={`${showError && 'labeled-item-error'}`}>
      <LabeledItem
        label={label}
        item={updatedItem}
        emptyState="--"
        // Matches the approved design: the warning icon sits next to the LABEL (not the
        // value, which stays its normal color), and the "Typical range: ..." text plus a
        // static outlier note render as their own line below the value. `item--warning` no
        // longer recolors the value (see labeled-item.scss) -- it's kept as a marker class
        // only, since existing tests/tooling key off its presence to detect out-of-range
        // fields.
        itemClassName={isOutOfRange ? 'item--warning' : ''}
        labelIcon={isOutOfRange && (
          <Icon iconName="warning" variant="warning" size="medium" className="required-labeled-item__warning-icon" />
        )}
        message={isOutOfRange && (
          <>
            {rangeMessage}
            <br />
            These values are considered outliers. Please validate.
          </>
        )}
      />
    </div>
  );
};

RequiredLabeledItem.propTypes = {
  controlType: PropTypes.oneOf([
    'text', 'boolean', 'date', 'select', 'textarea', 'number', 'clientCode', 'companySearch', 'naicsSearch', 'freeRent',
    'percentDollar', 'propertyAddressSearch', 'contactSearch', 'phone', 'zip', 'commissionAttachment', 'leaseEscalationSchedule',
  ]),
  editTarget: PropTypes.shape({
    reduxAction: PropTypes.string,
    fieldPath: PropTypes.string,
  }),
  fieldId: PropTypes.string,
  format: PropTypes.string,
  isReview: PropTypes.bool,
  item: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.any]),
  label: PropTypes.string.isRequired,
  linkTo: PropTypes.string,
  notRequired: PropTypes.bool,
  invalid: PropTypes.bool,
  // Options list for controlType="select", in the same `{value, display_name}` shape used
  // by the live voucher edit Tabs' own `Select` dropdowns (e.g. Lead Office, Rate Type).
  options: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string,
    display_name: PropTypes.string,
  })),
  section: PropTypes.string,
  subSection: PropTypes.string,
  // Resolved broker-verification bounds (BR-F2) for this field, when applicable -- see
  // `broker-verification-utils.js`. Advisory only; never affects `voucher_valid`.
  bounds: PropTypes.shape({
    category: PropTypes.string,
    min: PropTypes.number,
    max: PropTypes.number,
    minDate: PropTypes.string,
    maxDate: PropTypes.string,
  }),
  rangeFormat: PropTypes.string,
  rangeUnit: PropTypes.string,
  requiredMessage: PropTypes.string,
  forceRegister: PropTypes.bool,
  hideDisplay: PropTypes.bool,
  missingMessage: PropTypes.string,
  documents: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    name: PropTypes.string,
  })),
};

RequiredLabeledItem.defaultProps = {
  controlType: 'text',
  editTarget: null,
  fieldId: null,
  format: null,
  isReview: false,
  item: '',
  linkTo: null,
  notRequired: false,
  invalid: false,
  options: null,
  bounds: null,
  hideDisplay: false,
  missingMessage: null,
  documents: null,
  rangeFormat: null,
  rangeUnit: null,
  section: null,
  subSection: null,
  requiredMessage: null,
  forceRegister: false,
};

export default RequiredLabeledItem;
